import { uniqueId } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckDone } from '../../../assets/icons';
import { RoutePath } from '../../../routes';
import { Charger } from '../../../stores/types';
import { formatDateTimeRange } from '../../../utils/Date.Util';
import { Button, ButtonSize, ButtonType, ColorType, Divider, Label, LabelType } from '../../_ui';
import { SparkAnalyze } from './SparkAnalyze.component';
import { SparkLoading } from './SparkLoading.component';
import { formatSparkResultForClipboard } from './utils';

type SparkResultsProps = {
  charger: Charger;
  resultData?: any;
  dateTimeRange?: any;
  isSparkLimitExceed?: boolean;
};

export const SparkResults = ({
  charger,
  resultData,
  dateTimeRange,
  isSparkLimitExceed,
}: SparkResultsProps) => {
  const { t } = useTranslation();
  const copyToClickRef = useRef<any>(null);
  const [sparkResultData, setSparkResultData] = useState(resultData || null);
  const [isResultLoading, setIsResultLoading] = useState(false);
  const [copyState, setCopyState] = useState({
    isClipboardClicked: false,
    isCompleted: false,
    copiedContent: resultData?.copyToClipboardContent,
  });

  const copyToClipboard = () => {
    setCopyState((prev) => ({ ...prev, isClipboardClicked: true }));

    // Added charger name and analysis time to clipboard data
    const clipboardContent = `${t('charger_id_label')}: ${charger?.name}\n ${t(
      'spark_time_range_label',
    )}: ${formatDateTimeRange(dateTimeRange.selDates)} \n\n ${copyState.copiedContent}`;
    navigator.clipboard.writeText(clipboardContent).finally(() => {
      setTimeout(() => {
        setCopyState((prev) => ({
          ...prev,
          isClipboardClicked: false,
          isCompleted: true,
        }));
      }, 3000);
    });
  };

  const isSparkV3ResultFormat = useMemo(() => {
    return Array.isArray(sparkResultData);
  }, [sparkResultData]);

  const openPdfNewTab = () => {
    // Open a new tab to display the viewer
    const newTab = window.open(
      isSparkV3ResultFormat ? RoutePath.SPARK_RESULTS_V3 : RoutePath.SPARK_RESULTS,
      '_blank',
    );
    if (!newTab) {
      console.error('Failed to open new tab.');
    }
  };

  const renderDiagnosticSummary = () => {
    if (!sparkResultData?.diagnostic) {
      return null;
    }
    return (
      <div className='flex flex-col gap-1'>
        <Label
          type={LabelType.LABEL_M}
          text={t('spark_result_header_1')}
          color={ColorType.BLACK2}
        />
        <Label type={LabelType.BODY3} text={sparkResultData.diagnostic} color={ColorType.GREY6} />
      </div>
    );
  };

  const renderIssues = () => {
    if (!sparkResultData.issues) {
      return null;
    }
    return (
      <div className='flex flex-col gap-1'>
        <Label
          type={LabelType.LABEL_M}
          text={t('spark_result_header_2')}
          color={ColorType.BLACK2}
        />
        <Label type={LabelType.BODY3} text={sparkResultData.issues} color={ColorType.GREY6} />
      </div>
    );
  };

  const renderActionableInsights = () => {
    if (!sparkResultData?.insights?.length) {
      return null;
    }
    return (
      <div className='flex flex-col gap-1'>
        <Label
          type={LabelType.LABEL_M}
          text={t('spark_result_header_3')}
          color={ColorType.BLACK2}
        />
        {sparkResultData.insights.map((insight: string, indexNo: number) => {
          return (
            <div key={uniqueId()} className='flex flex-row gap-2'>
              <Label type={LabelType.BODY3} text={`${indexNo + 1}. `} color={ColorType.GREY6} />
              <Label type={LabelType.BODY3} text={insight} color={ColorType.GREY6} />
            </div>
          );
        })}
      </div>
    );
  };

  const renderReminders = () => {
    if (!sparkResultData?.reminders?.length) {
      return null;
    }
    return (
      <div className='flex flex-col gap-1'>
        <Label
          type={LabelType.LABEL_S_MEDIUM}
          text={t('spark_result_header_4')}
          color={ColorType.BLACK2}
        />
        {sparkResultData.reminders.map((insight: string, indexNo: number) => {
          return (
            <div key={uniqueId()} className='flex flex-row gap-2'>
              <Label type={LabelType.BODY3} text={`${indexNo + 1}. `} color={ColorType.GREY6} />
              <Label type={LabelType.BODY3} text={insight} color={ColorType.GREY6} />
            </div>
          );
        })}
      </div>
    );
  };

  const renderErrorDetected = (data: any) => {
    const { type, content } = data;
    return content.map((item: any) => (
      <div key={uniqueId()}>
        <div className='flex flex-col gap-2'>
          <div className='flex flex-row items-center gap-1'>
            <Label type={LabelType.LABEL_M} text={`${type}: `} />
            <Label type={LabelType.BODY3} text={item.signalName} />
          </div>
          {item.severity && item.events && (
            <div className='flex flex-row items-center gap-4'>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_severity')}:`} />
                <Label type={LabelType.BODY3} text={item.severity} />
              </div>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_events')}:`} />
                <Label type={LabelType.BODY3} text={item.events} />
              </div>
            </div>
          )}
          {item.possible_causes?.length > 0 && (
            <div className='flex flex-col gap-1'>
              <Label
                type={LabelType.LABEL_S_MEDIUM}
                text={`${t('spark_result_possible_cause')}:`}
              />
              {item.possible_causes.map((cause: string, causeIndex: number) => (
                <div key={cause} className='flex flex-row gap-2'>
                  <Label
                    type={LabelType.BODY3}
                    text={`${causeIndex + 1}. `}
                    color={ColorType.GREY6}
                  />
                  <Label type={LabelType.BODY3} text={cause} color={ColorType.GREY6} />
                </div>
              ))}
            </div>
          )}
          {item.how_to_troubleshoot?.length > 0 && (
            <div className='flex flex-col gap-1'>
              <Label
                type={LabelType.LABEL_S_MEDIUM}
                text={`${t('spark_result_how_to_troubleshoot')}: `}
              />
              {item.how_to_troubleshoot.map((step: string, tsIndex: number) => (
                <div key={step} className='flex flex-row gap-2'>
                  <Label type={LabelType.BODY3} text={`${tsIndex + 1}. `} color={ColorType.GREY6} />
                  <Label type={LabelType.BODY3} text={step} color={ColorType.GREY6} />
                </div>
              ))}
            </div>
          )}
          {item.name && (
            <div className='flex flex-col gap-1'>
              <div className='flex flex-row items-center gap-1'>
                <Label type={LabelType.LABEL_M} text={`${t('spark_result_name')}:`} />
                <Label type={LabelType.BODY3} text={item.name} />
              </div>
            </div>
          )}
          {item.definition && (
            <div className='flex flex-col gap-1'>
              <Label type={LabelType.LABEL_S_MEDIUM} text={`${t('spark_result_definition')}:`} />
              <Label type={LabelType.BODY3} text={item.definition} />
            </div>
          )}
        </div>

        <Divider className='my-4' />
      </div>
    ));
  };

  const renderSignalGlossary = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        {content.map((item: any) => (
          <div key={uniqueId()} className='flex flex-col gap-2'>
            {item.name && item.definition && (
              <div className='flex flex-col gap-1'>
                <div className='flex flex-col gap-1'>
                  <Label type={LabelType.LABEL_S_MEDIUM} text={`${item.name}:`} />
                  <Label type={LabelType.BODY3} text={item.definition} />
                </div>
              </div>
            )}
          </div>
        ))}
        <Divider className='my-4' />
      </div>
    );
  };

  const renderSignalSeverityDefinitions = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        {content.map((item: any) => (
          <div key={uniqueId()} className='flex flex-col gap-2'>
            {item.name && item.definition && (
              <div className='flex flex-col gap-1'>
                <div className='flex flex-col gap-1'>
                  <Label type={LabelType.LABEL_S_MEDIUM} text={`${item.name}:`} />
                  <Label type={LabelType.BODY3} text={item.definition} />
                </div>
              </div>
            )}
          </div>
        ))}
        <Divider className='my-4' />
      </div>
    );
  };

  const renderSummary = (data: any) => {
    const { type, content } = data;
    return (
      <div key={uniqueId()} className='flex flex-col gap-2'>
        <Label type={LabelType.LABEL_M} text={`${type}:`} />
        <Label type={LabelType.BODY3} text={content.length > 0 ? content[0].text : ''} />
      </div>
    );
  };

  const renderResultV3 = () => {
    if (isResultLoading) {
      return (
        <div className='flex items-center justify-center'>
          <SparkLoading />
        </div>
      );
    }

    if (!sparkResultData?.length) {
      return null;
    }
    return (
      <div className='flex flex-col gap-4'>
        {sparkResultData.map((data: any) => {
          switch (data.type.toUpperCase()) {
            case 'ERROR DETECTED':
              return renderErrorDetected(data);
            case 'SIGNAL GLOSSARY':
              return renderSignalGlossary(data);
            case 'SIGNAL SEVERITY DEFINITIONS':
              return renderSignalSeverityDefinitions(data);
            case 'SUMMARY':
              return renderSummary(data);
            default:
              return null;
          }
        })}
      </div>
    );
  };

  const renderResultV1 = () => {
    if (isResultLoading) {
      return (
        <div className='flex items-center justify-center'>
          <SparkLoading />
        </div>
      );
    }
    if (!sparkResultData) {
      return null;
    }
    return (
      <div className='flex flex-col gap-4'>
        {renderDiagnosticSummary()}
        {renderIssues()}
        {renderActionableInsights()}
        {renderReminders()}
      </div>
    );
  };

  const renderPrintCopyButton = () => {
    if (isResultLoading) {
      return null;
    }
    return (
      <div
        data-html2canvas-ignore='true'
        className='flex flex-row gap-2 sticky bottom-0 bg-white py-5'
      >
        <Button
          labelContainerCss='grow-0'
          icon={copyState.isCompleted ? CheckDone : undefined}
          iconColor={ColorType.BRAND2}
          type={ButtonType.SECONDARY}
          label={copyState.isCompleted ? t('clipboard_copied') : t('copy_to_clipboard')}
          size={ButtonSize.MEDIUM_FULL}
          onClick={copyToClipboard}
          isSumbit
          isLoading={copyState.isClipboardClicked}
        />
        <Button
          type={ButtonType.PRIMARY}
          label={t('open_in_new_tab')}
          size={ButtonSize.MEDIUM_FULL}
          onClick={openPdfNewTab}
          isSumbit
          isLoading={false}
        />
      </div>
    );
  };

  useEffect(() => {
    if (isResultLoading) {
      setCopyState((prev) => ({
        ...prev,
        isClipboardClicked: false,
        isCompleted: false,
      }));
    }
  }, [isResultLoading]);

  useEffect(() => {
    if (isSparkV3ResultFormat) {
      if (sparkResultData) {
        setCopyState((prev) => ({
          ...prev,
          copiedContent: formatSparkResultForClipboard(sparkResultData),
        }));
      }
    } else {
      if (sparkResultData?.copyToClipboardContent) {
        setCopyState((prev) => ({
          ...prev,
          copiedContent: sparkResultData?.copyToClipboardContent,
        }));
      }
    }
  }, [sparkResultData, isSparkV3ResultFormat]);

  useEffect(() => {
    if (sparkResultData) {
      if (isSparkV3ResultFormat) {
        sessionStorage.setItem('sparkResultDataV3', JSON.stringify(sparkResultData));
      } else {
        sessionStorage.setItem('sparkResultData', JSON.stringify(sparkResultData));
      }
      sessionStorage.setItem('charger', JSON.stringify(charger));
      sessionStorage.setItem('dateTimeRange', JSON.stringify(dateTimeRange));
    }
  }, [sparkResultData, isSparkV3ResultFormat, charger, dateTimeRange]);

  return (
    <>
      <div ref={copyToClickRef} className='flex flex-col gap-4' id='print-container'>
        <div className='flex flex-row gap-8'>
          <Label text={t('charger_id_label')} type={LabelType.LABEL_S} className='min-w-[80px]' />
          <Label text={charger.name} type={LabelType.BODY3} />
        </div>
        <SparkAnalyze
          isSparkLimitExceed={isSparkLimitExceed}
          selectedDates={dateTimeRange}
          charger={charger}
          setIsResultLoading={setIsResultLoading}
          setSparkResultData={setSparkResultData}
          isModelView
        />
        <Divider />
        {isSparkV3ResultFormat ? renderResultV3() : renderResultV1()}
      </div>
      {renderPrintCopyButton()}
    </>
  );
};
